import axios from 'axios';

/**
 * File Upload Service Configuration
 * 
 * This service handles all file upload/download operations for the payslip system,
 * including salary data, bonus data, and employee information management.
 * 
 * The service provides:
 * - File upload with progress tracking
 * - Data validation before upload
 * - CRUD operations for salary and bonus data
 * - CSV download functionality
 * - Department and employee data management
 */

import { FILE_UPLOAD_API_URL } from '../config/apiConfig';
import { fileParserUtils } from '../utils/FileParserUtils';
import { CONSTRUCTION_BONUS_API_BASE_PATH } from '../utils/constructionBonusContract';

// Base URL for API endpoints - now dynamically determined
const API_BASE_URL = FILE_UPLOAD_API_URL;

/**
 * Response interface for file upload operations
 * Provides standardized response format with success status, message, and optional data/errors
 */
export interface UploadResponse {
  success: boolean;        // Whether the upload operation was successful
  message: string;         // Human-readable message describing the result
  data?: any;             // Optional response data from the server
  errors?: string[];       // Optional array of error messages
}

/**
 * Response interface for file validation operations
 * Used when validating files before upload to ensure data integrity
 */
export interface ValidationResponse {
  isValid: boolean;        // Whether the file content is valid
  message: string;         // Validation result message
  preview?: any[];         // Optional preview of the file content
  errors?: string[];       // Optional array of validation errors
}

/**
 * Interface for tracking file upload progress
 * Used to provide real-time feedback during large file uploads
 */
export interface FileUploadProgress {
  loaded: number;          // Number of bytes uploaded so far
  total: number;           // Total file size in bytes
  percentage: number;      // Upload progress as a percentage (0-100)
}

/**
 * Options for CSV download operations
 * Allows filtering data by various criteria before download
 */
export interface CsvDownloadOptions {
  dataType: 'salary' | 'bonus';  // Type of data to download
  year?: number;                  // Optional year filter
  month?: number;                 // Optional month filter (for salary data)
  period?: string;                // Optional period filter (for bonus data)
  department?: string;            // Optional department filter
  employeeCode?: string;          // Optional employee code filter
}

/**
 * Department information structure
 * Used for organizing employees and filtering data
 */
export interface Department {
  id: string;              // Unique department identifier
  name: string;            // Human-readable department name
}

/**
 * Employee information structure
 * Basic employee data used throughout the system
 */
export interface Employee {
  code: string;            // Employee identification code
  name: string;            // Employee full name
  department: string;      // Department the employee belongs to
}

/**
 * FileUploadService Class
 * 
 * Main service class that handles all file operations and data management.
 * Uses axios for HTTP requests with configurable timeout and error handling.
 */
class FileUploadService {
  // Axios instance configured with base URL and timeout settings
  private api = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000, // 30 seconds timeout for file uploads (prevents hanging on slow connections)
  });

  constructor() {
    // Add request interceptor to include JWT token in all requests
    this.api.interceptors.request.use(
      (config) => {
        const token = sessionStorage.getItem('authToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        // Don't override Content-Type for FormData - let browser/axios set it automatically
        // This ensures proper multipart/form-data boundary is set
        if (config.data instanceof FormData && config.headers['Content-Type']) {
          delete config.headers['Content-Type'];
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor to handle authentication errors
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Token expired or invalid, clear it and redirect to login
          sessionStorage.removeItem('authToken');
          sessionStorage.removeItem('currentEmployee');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Uploads a salary data file to the server
   * 
   * @param file - The file to upload (CSV format expected)
   * @param year - Year associated with the salary data
   * @param month - Month associated with the salary data
   * @param onProgress - Optional callback for tracking upload progress
   * @returns Promise<UploadResponse> - Result of the upload operation
   * 
   * This method:
   * - Creates FormData with file and metadata
   * - Sends POST request to /salary/upload endpoint
   * - Tracks upload progress if callback provided
   * - Returns standardized response format
   */
  async uploadSalaryFile(
    file: File, 
    year: number, 
    month: number, 
    onProgress?: (progress: FileUploadProgress) => void
  ): Promise<UploadResponse> {
    try {
      // Convert file to UTF-8 before uploading to ensure proper encoding
      // This fixes issues with extended CJK characters like "濵" (U+6F35)
      console.log('Converting file to UTF-8 before upload...');
      const utf8File = await fileParserUtils.convertFileToUTF8(file);
      console.log('File converted to UTF-8, original size:', file.size, 'new size:', utf8File.size);
      
      // Prepare form data with UTF-8 converted file and metadata
      const formData = new FormData();
      formData.append('file', utf8File);
      formData.append('year', year.toString());
      formData.append('month', month.toString());

      // Send upload request with progress tracking
      // Note: Don't set Content-Type manually when using FormData - axios will set it with boundary
      const response = await this.api.post('/salary/upload', formData, {
        onUploadProgress: (progressEvent) => {
          // Calculate and report upload progress if callback provided
          if (onProgress && progressEvent.total) {
            onProgress({
              loaded: progressEvent.loaded,
              total: progressEvent.total,
              percentage: Math.round((progressEvent.loaded * 100) / progressEvent.total)
            });
          }
        },
      });

      // Return success response with server data
      return {
        success: true,
        message: '給与ファイルのアップロードが完了しました',
        data: response.data
      };
    } catch (error: any) {
      // Return error response with details from server or fallback message
      return {
        success: false,
        message: error.response?.data?.message || '給与ファイルのアップロードに失敗しました',
        errors: error.response?.data?.errors || []
      };
    }
  }

  /**
   * Uploads a bonus data file to the server
   * 
   * @param file - The file to upload (CSV format expected)
   * @param year - Year associated with the bonus data
   * @param bonusPeriod - Bonus period identifier (e.g., "Summer", "Winter")
   * @param onProgress - Optional callback for tracking upload progress
   * @returns Promise<UploadResponse> - Result of the upload operation
   * 
   * Similar to salary upload but handles bonus-specific data structure
   */
  async uploadBonusFile(
    file: File, 
    year: number, 
    bonusPeriod: string,
    onProgress?: (progress: FileUploadProgress) => void
  ): Promise<UploadResponse> {
    try {
      // Convert file to UTF-8 before uploading
      // This fixes issues with extended CJK characters like "濵" (U+6F35)
      console.log('Converting bonus file to UTF-8 before upload...');
      const utf8File = await fileParserUtils.convertFileToUTF8(file);
      console.log('Bonus file converted to UTF-8');
      
      // Prepare form data with UTF-8 converted file and bonus-specific metadata
      const formData = new FormData();
      formData.append('file', utf8File);
      formData.append('year', year.toString());
      formData.append('period', bonusPeriod);

      // Send upload request with progress tracking
      // Note: Don't set Content-Type manually when using FormData - axios will set it with boundary
      const response = await this.api.post('/bonus/upload', formData, {
        onUploadProgress: (progressEvent) => {
          // Calculate and report upload progress if callback provided
          if (onProgress && progressEvent.total) {
            onProgress({
              loaded: progressEvent.loaded,
              total: progressEvent.total,
              percentage: Math.round((progressEvent.loaded * 100) / progressEvent.total)
            });
          }
        },
      });

      // Return success response with server data
      return {
        success: true,
        message: '賞与ファイルのアップロードが完了しました',
        data: response.data
      };
    } catch (error: any) {
      // Return error response with details from server or fallback message
      return {
        success: false,
        message: error.response?.data?.message || '賞与ファイルのアップロードに失敗しました',
        errors: error.response?.data?.errors || []
      };
    }
  }

  /**
   * Validates a file before upload to ensure data integrity
   * 
   * @param file - The file to validate
   * @param fileType - Type of file ('salary' or 'bonus')
   * @returns Promise<ValidationResponse> - Validation result with preview and errors
   * 
   * This method:
   * - Sends file to server for validation
   * - Returns validation status and any errors found
   * - Provides data preview for user confirmation
   */
  async validateFile(
    file: File, 
    fileType: 'salary' | 'bonus'
  ): Promise<ValidationResponse> {
    try {
      // Prepare form data for validation request
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', fileType);

      // Send validation request to server
      const response = await this.api.post('/validate/file', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Return validation result with preview and errors
      return {
        isValid: response.data.valid,
        message: response.data.message,
        preview: response.data.preview,
        errors: response.data.errors
      };
    } catch (error: any) {
      // Return validation failure with error details
      return {
        isValid: false,
        message: error.response?.data?.message || 'ファイルの検証に失敗しました',
        errors: error.response?.data?.errors || []
      };
    }
  }

  /**
   * Checks if salary data already exists for a specific year and month
   * 
   * @param year - Year to check
   * @param month - Month to check
   * @returns Promise<boolean> - True if data exists, false otherwise
   * 
   * Used to prevent duplicate data uploads and warn users
   */
  async checkSalaryDataExists(year: number, month: number): Promise<boolean> {
    try {
      const response = await this.api.get(`/salary/data?year=${year}&month=${month}`);
      return response.data && response.data.length > 0;
    } catch (error: any) {
      // If there's an error (like 404), it means no data exists
      return false;
    }
  }

  /**
   * Checks if bonus data already exists for a specific year and period
   * 
   * @param year - Year to check
   * @param period - Bonus period to check
   * @returns Promise<boolean> - True if data exists, false otherwise
   * 
   * Used to prevent duplicate bonus data uploads
   */
  async checkBonusDataExists(year: number, period: string): Promise<boolean> {
    try {
      const response = await this.api.get(`/bonus/data?year=${year}&period=${period}`);
      return response.data && response.data.length > 0;
    } catch (error: any) {
      // If there's an error (like 404), it means no data exists
      return false;
    }
  }

  /**
   * Retrieves salary data for a specific year and month
   * 
   * @param year - Year of salary data
   * @param month - Month of salary data
   * @returns Promise<any> - Salary data from server
   * @throws Error if data retrieval fails
   */
  async getSalaryData(year: number, month: number): Promise<any> {
    try {
      const response = await this.api.get(`/salary/data?year=${year}&month=${month}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch salary data');
    }
  }

  /**
   * Retrieves all salary data from the system
   * 
   * @returns Promise<any> - All salary data
   * @throws Error if data retrieval fails
   * 
   * Use with caution - may return large datasets
   */
  async getAllSalaryData(): Promise<any> {
    try {
      const response = await this.api.get('/salary/data');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch salary data');
    }
  }

  /**
   * Retrieves salary data for a specific year
   * 
   * @param year - Year of salary data
   * @returns Promise<any> - Salary data for the specified year
   * @throws Error if data retrieval fails
   */
  async getSalaryDataByYear(year: number): Promise<any> {
    try {
      const response = await this.api.get(`/salary/data?year=${year}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch salary data');
    }
  }

  /**
   * Retrieves bonus data for a specific year and period
   * 
   * @param year - Year of bonus data
   * @param period - Bonus period identifier
   * @returns Promise<any> - Bonus data from server
   * @throws Error if data retrieval fails
   */
  async getBonusData(year: number, period: string): Promise<any> {
    try {
      const response = await this.api.get(`/bonus/data?year=${year}&period=${period}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch bonus data');
    }
  }

  /**
   * Retrieves all bonus data from the system
   * 
   * @returns Promise<any> - All bonus data
   * @throws Error if data retrieval fails
   * 
   * Use with caution - may return large datasets
   */
  async getAllBonusData(): Promise<any> {
    try {
      const response = await this.api.get('/bonus/data');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch bonus data');
    }
  }

  /**
   * Comprehensive function to fetch salary data with multiple filter options
   * 
   * @param year - Optional year filter
   * @param month - Optional month filter
   * @param department - Optional department filter
   * @param employeeCode - Optional employee code filter
   * @returns Promise<any> - Filtered salary data
   * @throws Error if data retrieval fails
   * 
   * This method builds a dynamic query URL based on provided filters
   */
  async fetchSalaryDataWithFilters(year?: number, month?: number, department?: string, employeeCode?: string): Promise<any> {
    try {
      let url = `${API_BASE_URL}/salary/data`;
      const params = new URLSearchParams();
      
      // Add filters to query parameters if provided
      if (year) params.append('year', year.toString());
      if (month) params.append('month', month.toString());
      if (department) params.append('department', department);
      if (employeeCode) params.append('employeeCode', employeeCode);
      
      // Append query string if parameters exist
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await this.api.get(url);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch salary data');
    }
  }

  /**
   * Comprehensive function to fetch bonus data with multiple filter options
   * 
   * @param year - Optional year filter
   * @param period - Optional period filter
   * @param department - Optional department filter
   * @param employeeCode - Optional employee code filter
   * @returns Promise<any> - Filtered bonus data
   * @throws Error if data retrieval fails
   * 
   * Similar to salary data filtering but for bonus data
   */
  async fetchBonusDataWithFilters(year?: number, period?: string, department?: string, employeeCode?: string): Promise<any> {
    try {
      let url = `${API_BASE_URL}/bonus/data`;
      const params = new URLSearchParams();
      
      // Add filters to query parameters if provided
      if (year) params.append('year', year.toString());
      if (period) params.append('period', period);
      if (department) params.append('department', department);
      if (employeeCode) params.append('employeeCode', employeeCode);
      
      // Append query string if parameters exist
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await this.api.get(url);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch bonus data');
    }
  }

  /**
   * Deletes salary data based on specified criteria
   * 
   * @param year - Optional year filter for deletion
   * @param month - Optional month filter for deletion
   * @param employeeCode - Optional employee code filter for deletion
   * @returns Promise<any> - Deletion result from server
   * @throws Error if deletion fails
   * 
   * WARNING: This operation is irreversible and will permanently delete data
   */
  async deleteSalaryData(year?: number, month?: number, employeeCode?: string): Promise<any> {
    try {
      let url = `${API_BASE_URL}/salary/data`;
      const params = new URLSearchParams();
      
      // Build deletion criteria
      if (year) params.append('year', year.toString());
      if (month) params.append('month', month.toString());
      if (employeeCode) params.append('employeeCode', employeeCode);
      
      // Append query string if parameters exist
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await this.api.delete(url);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to delete salary data');
    }
  }

  /**
   * Deletes bonus data based on specified criteria
   * 
   * @param year - Optional year filter for deletion
   * @param period - Optional period filter for deletion
   * @returns Promise<any> - Deletion result from server
   * @throws Error if deletion fails
   * 
   * WARNING: This operation is irreversible and will permanently delete data
   */
  async deleteBonusData(year?: number, period?: string, employeeCode?: string): Promise<any> {
    try {
      let url = `${API_BASE_URL}/bonus/data`;
      const params = new URLSearchParams();
      
      // Build deletion criteria
      if (year) params.append('year', year.toString());
      if (period) params.append('period', period);
      if (employeeCode) params.append('employeeCode', employeeCode);
      
      // Append query string if parameters exist
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await this.api.delete(url);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to delete bonus data');
    }
  }

  /**
   * Deletes a specific individual bonus record
   * 
   * @param year - Year of the bonus record
   * @param period - Period of the bonus record
   * @param employeeCode - Employee code of the record to delete
   * @returns Promise<any> - Deletion result from server
   * @throws Error if deletion fails
   * 
   * This method provides granular control for deleting specific records
   */
  async deleteIndividualBonusRecord(year: number, period: string, employeeCode: string): Promise<any> {
    try {
      let url = `${API_BASE_URL}/bonus/data`;
      const params = new URLSearchParams();
      
      // Build specific deletion criteria
      params.append('year', year.toString());
      params.append('period', period);
      params.append('employeeCode', employeeCode);
      
      url += `?${params.toString()}`;

      const response = await this.api.delete(url);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to delete individual bonus record');
    }
  }

  /**
   * Deletes all bonus data for a specific employee
   * 
   * @param employeeCode - Employee code whose bonus data should be deleted
   * @returns Promise<any> - Deletion result from server
   * @throws Error if deletion fails
   * 
   * WARNING: This will delete ALL bonus records for the specified employee
   */
  async deleteBonusDataByEmployee(employeeCode: string): Promise<any> {
    try {
      let url = `${API_BASE_URL}/bonus/data`;
      const params = new URLSearchParams();
      
      // Build deletion criteria for specific employee
      params.append('employeeCode', employeeCode);
      
      url += `?${params.toString()}`;

      const response = await this.api.delete(url);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to delete bonus data by employee');
    }
  }

  /**
   * Fetches employee information by employee code
   * @param employeeCode - The employee code to search for
   * @returns Promise<Employee> - Employee information
   * @throws Error if employee fetch fails
   * 
   * Used for payslip generation to get employee details
   */
  async getEmployeeByCode(employeeCode: string): Promise<Employee> {
    try {
      
      const response = await this.api.get(`/employees/${employeeCode}`);
      
      return response.data;
    } catch (error: any) {
      
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
      } else if (error.request) {
        // The request was made but no response was received
      } else {
        // Something happened in setting up the request that triggered an Error
      }
      
      throw new Error(error.response?.data?.message || '従業員情報の取得に失敗しました。');
    }
  }

  /**
   * Fetches payment data for a specific employee, year, and month
   * @param employeeCode - Employee code
   * @param year - Year of salary data
   * @param month - Month of salary data
   * @returns Promise<any> - Payment data for the employee
   * @throws Error if data retrieval fails
   * 
   * Used for payslip generation to populate payment section
   */
  async getPaymentData(employeeCode: string, year: number, month: number, employeeName?: string): Promise<any> {
    try {
      const params = new URLSearchParams({ year: String(year), month: String(month) });
      if (employeeName != null) params.set('employeeName', employeeName);
      else params.set('employeeCode', employeeCode);
      const response = await this.api.get(`/salary/data?${params.toString()}`);
      const data = response.data;
      
      // Return the first (and should be only) record for this employee
      if (data && data.length > 0) {
        const salaryData = data[0];
        // Return only payment-related fields
        return {
          baseSalary: salaryData.baseSalary,
          positionAllowance: salaryData.positionAllowance,
          familyAllowance: salaryData.familyAllowance,
          housingAllowance: salaryData.housingAllowance,
          qualificationAllowance: salaryData.qualificationAllowance,
          diligenceAllowance: salaryData.diligenceAllowance,
          fieldAllowance: salaryData.fieldAllowance,
          overtimeAllowance: salaryData.overtimeAllowance,
          otherAllowance: salaryData.otherAllowance,
          yearEndAdjustment: salaryData.yearEndAdjustment,
          longServiceAward: salaryData.longServiceAward,
          totalOtherAllowances: salaryData.totalOtherAllowances,
          regularOvertime: salaryData.regularOvertime,
          nightOvertime: salaryData.nightOvertime,
          holidayOvertime: salaryData.holidayOvertime,
          holidayNight: salaryData.holidayNight,
          legalHolidayOvertimeAmount: salaryData.legalHolidayOvertimeAmount,
          legalHolidayNightAmount: salaryData.legalHolidayNightAmount,
          legalOvertimeAmount: salaryData.legalOvertimeAmount,
          absenceDeduction: salaryData.absenceDeduction,
          lateEarlyDeduction: salaryData.lateEarlyDeduction,
          taxableCommutingExpense: salaryData.taxableCommutingExpense,
          nonTaxableCommutingExpense: salaryData.nonTaxableCommutingExpense,
          taxableMealCost: salaryData.taxableMealCost,
          nonTaxableMealCost: salaryData.nonTaxableMealCost,
          taxableNightDuty: salaryData.taxableNightDuty,
          nonTaxableNightDuty: salaryData.nonTaxableNightDuty,
          taxableSalaryIncrease: salaryData.taxableSalaryIncrease,
          nonTaxableSalaryIncrease: salaryData.nonTaxableSalaryIncrease,
          totalPayment: salaryData.totalPayment,
          taxablePaymentCumulative: salaryData.taxablePaymentCumulative,
          personalMessage1: salaryData.personalMessage1,
          personalMessage2: salaryData.personalMessage2,
          department: salaryData.department
        };
      }
      
      return null;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '支給データの取得に失敗しました。');
    }
  }

  /**
   * Fetches deduction data for a specific employee, year, and month
   * @param employeeCode - Employee code
   * @param year - Year of salary data
   * @param month - Month of salary data
   * @returns Promise<any> - Deduction data for the employee
   * @throws Error if data retrieval fails
   * 
   * Used for payslip generation to populate deduction section
   */
  async getDeductionData(employeeCode: string, year: number, month: number, employeeName?: string): Promise<any> {
    try {
      const params = new URLSearchParams({ year: String(year), month: String(month) });
      if (employeeName != null) params.set('employeeName', employeeName);
      else params.set('employeeCode', employeeCode);
      const response = await this.api.get(`/salary/data?${params.toString()}`);
      const data = response.data;
      
      // Return the first (and should be only) record for this employee
      if (data && data.length > 0) {
        const salaryData = data[0];
        // Return only deduction-related fields
        return {
          healthInsurance: salaryData.healthInsurance,
          longTermCareInsurance: salaryData.longTermCareInsurance,
          pensionInsurance: salaryData.pensionInsurance,
          welfareFund: salaryData.welfareFund,
          employmentInsurance: salaryData.employmentInsurance,
          taxableAmount: salaryData.taxableAmount,
          incomeTax: salaryData.incomeTax,
          residenceTax: salaryData.residenceTax,
          residenceTaxCorrection: salaryData.residenceTaxCorrection,
          employeeSavings: salaryData.employeeSavings,
          rent: salaryData.rent,
          totalDeductions: salaryData.totalDeductions,
          socialInsuranceDeductionCumulative: salaryData.socialInsuranceDeductionCumulative,
          incomeTaxCumulative: salaryData.incomeTaxCumulative
        };
      }
      
      return null;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '控除データの取得に失敗しました。');
    }
  }

  /**
   * Fetches attendance data for a specific employee, year, and month
   * @param employeeCode - Employee code
   * @param year - Year of salary data
   * @param month - Month of salary data
   * @returns Promise<any> - Attendance data for the employee
   * @throws Error if data retrieval fails
   * 
   * Used for payslip generation to populate attendance section
   */
  async getAttendanceData(employeeCode: string, year: number, month: number, employeeName?: string): Promise<any> {
    try {
      const params = new URLSearchParams({ year: String(year), month: String(month) });
      if (employeeName != null) params.set('employeeName', employeeName);
      else params.set('employeeCode', employeeCode);
      const response = await this.api.get(`/salary/data?${params.toString()}`);
      const data = response.data;
      
      // Return the first (and should be only) record for this employee
      if (data && data.length > 0) {
        const salaryData = data[0];
        // Return only attendance-related fields
        return {
          workingDays: salaryData.workingDays,
          attendanceDays: salaryData.attendanceDays,
          absenceDays: salaryData.absentDays,
          paidLeaveDays: salaryData.paidLeaveDays,
          specialLeaveDays: salaryData.specialLeaveDays,
          holidayWorkDays: salaryData.holidayWorkDays,
          legalHolidayWorkDays: salaryData.legalHolidayWorkDays,
          compensatoryLeaveDays: salaryData.compensatoryLeaveDays,
          mealCount: salaryData.mealCount,
          nightDutyCount: salaryData.nightDutyCount,
          lateEarlyCount: salaryData.lateEarlyCount,
          attendanceHours: salaryData.attendanceHours,
          lateEarlyHours: salaryData.lateEarlyTime,
          regularOvertimeHours: salaryData.regularOvertimeHours,
          nightOvertimeHours: salaryData.nightOvertimeHours,
          holidayOvertimeHours: salaryData.holidayOvertimeHours,
          holidayNightHours: salaryData.holidayNightTime,
          legalHolidayOvertimeHours: salaryData.legalHolidayOvertimeHours,
          legalHolidayNightHours: salaryData.legalHolidayNightTime,
          legalOvertimeHours: salaryData.legalOvertimeHours,
          dependentsCount: salaryData.numberOfDependentsEtc,
          remainingPaidLeave: salaryData.remainingPaidLeaveDays
        };
      }
      
      return null;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '勤怠データの取得に失敗しました。');
    }
  }

  /**
   * Fetches salary notes data for a specific employee, year, and month
   * @param employeeCode - Employee code
   * @param year - Year of salary data
   * @param month - Month of salary data
   * @returns Promise<any> - Salary notes data for the employee
   * @throws Error if data retrieval fails
   * 
   * Used for payslip generation to populate notes section
   */
  async getSalaryNotesData(employeeCode: string, year: number, month: number, employeeName?: string): Promise<any> {
    try {
      const params = new URLSearchParams({ year: String(year), month: String(month) });
      if (employeeName != null) params.set('employeeName', employeeName);
      else params.set('employeeCode', employeeCode);
      const response = await this.api.get(`/salary/data?${params.toString()}`);
      const data = response.data;
      
      // Return the first (and should be only) record for this employee
      if (data && data.length > 0) {
        const salaryData = data[0];
        // Return only notes-related fields
        return {
          actualPayment: salaryData.actualPayment,
          bankTransfer1: salaryData.bankTransfer1,
          bankTransfer2: salaryData.bankTransfer2,
          bankTransfer3: salaryData.bankTransfer3,
          cashPayment: salaryData.cashPayment,
          inKindPayment: salaryData.inKindPayment,
          yearEndAdjustment: salaryData.yearEndAdjustment,
          taxableAmount: salaryData.taxableAmount,
          deductedPayment: salaryData.deductedPayment
        };
      }
      
      return null;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '給与記事データの取得に失敗しました。');
    }
  }

  /**
   * Fetches bonus payment data for a specific employee, year, and period
   * @param employeeCode - Employee code
   * @param year - Year of bonus data
   * @param period - Bonus period
   * @returns Promise<any> - Bonus payment data for the employee
   * @throws Error if data retrieval fails
   * 
   * Used for payslip generation to populate bonus payment section
   */
  async getBonusPaymentData(employeeCode: string, year: number, period: string, employeeName?: string): Promise<any> {
    try {
      const params = new URLSearchParams({ year: String(year), period });
      if (employeeName != null) params.set('employeeName', employeeName);
      else params.set('employeeCode', employeeCode);
      const response = await this.api.get(`/bonus/data?${params.toString()}`);
      const data = response.data;
      
      // Return the first (and should be only) record for this employee
      if (data && data.length > 0) {
        return data[0];
      }
      
      return null;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '賞与支給データの取得に失敗しました。');
    }
  }

  /**
   * Fetches bonus deduction data for a specific employee, year, and period
   * @param employeeCode - Employee code
   * @param year - Year of bonus data
   * @param period - Bonus period
   * @returns Promise<any> - Bonus deduction data for the employee
   * @throws Error if data retrieval fails
   * 
   * Used for payslip generation to populate bonus deduction section
   */
  async getBonusDeductionData(employeeCode: string, year: number, period: string, employeeName?: string): Promise<any> {
    try {
      const params = new URLSearchParams({ year: String(year), period });
      if (employeeName != null) params.set('employeeName', employeeName);
      else params.set('employeeCode', employeeCode);
      const response = await this.api.get(`/bonus/data?${params.toString()}`);
      const data = response.data;
      
      // Return the first (and should be only) record for this employee
      if (data && data.length > 0) {
        return data[0];
      }
      
      return null;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '賞与控除データの取得に失敗しました。');
    }
  }

  /**
   * Fetches bonus attendance data for a specific employee, year, and period
   * @param employeeCode - Employee code
   * @param year - Year of bonus data
   * @param period - Bonus period
   * @returns Promise<any> - Bonus attendance data for the employee
   * @throws Error if data retrieval fails
   * 
   * Used for payslip generation to populate bonus attendance section
   */
  async getBonusAttendanceData(employeeCode: string, year: number, period: string, employeeName?: string): Promise<any> {
    try {
      const params = new URLSearchParams({ year: String(year), period });
      if (employeeName != null) params.set('employeeName', employeeName);
      else params.set('employeeCode', employeeCode);
      const response = await this.api.get(`/bonus/data?${params.toString()}`);
      const data = response.data;
      
      // Return the first (and should be only) record for this employee
      if (data && data.length > 0) {
        return data[0];
      }
      
      return null;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '賞与勤怠データの取得に失敗しました。');
    }
  }

  /**
   * Fetches bonus notes data for a specific employee, year, and period
   * @param employeeCode - Employee code
   * @param year - Year of bonus data
   * @param period - Bonus period
   * @returns Promise<any> - Bonus notes data for the employee
   * @throws Error if data retrieval fails
   * 
   * Used for payslip generation to populate bonus notes section
   */
  async getBonusNotesData(employeeCode: string, year: number, period: string, employeeName?: string): Promise<any> {
    try {
      const params = new URLSearchParams({ year: String(year), period });
      if (employeeName != null) params.set('employeeName', employeeName);
      else params.set('employeeCode', employeeCode);
      const response = await this.api.get(`/bonus/data?${params.toString()}`);
      const data = response.data;
      
      
      // Return the first (and should be only) record for this employee
      if (data && data.length > 0) {
        return data[0];
      }
      
      return null;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '賞与記事データの取得に失敗しました。');
    }
  }

  // Construction Worker Data Fetching Methods

  /**
   * Fetches construction salary data with optional filters
   * @param genbaName - Optional construction site name filter
   * @param year - Optional year filter
   * @param month - Optional month filter
   * @param employeeName - Optional employee name filter
   * @returns Promise<any> - Construction salary data
   * @throws Error if data retrieval fails
   */
  async fetchConstructionDataWithFilters(genbaName?: string, year?: number, month?: number, employeeName?: string): Promise<any> {
    try {
      let url = `${API_BASE_URL}/construction-salary/data`;
      const params = new URLSearchParams();
      
      // Add filters to query parameters if provided
      if (genbaName) params.append('genbaName', genbaName);
      if (year) params.append('year', year.toString());
      if (month) params.append('month', month.toString());
      if (employeeName) params.append('employeeName', employeeName);
      
      // Append query string if parameters exist
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await this.api.get(url);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '建設データの取得に失敗しました');
    }
  }

  /**
   * Fetches available years from construction salary data
   * @returns Promise<number[]> - Array of available years
   * @throws Error if data retrieval fails
   */
  async getConstructionAvailableYears(): Promise<number[]> {
    try {
      const response = await this.api.get(`${API_BASE_URL}/construction-salary/years`);
      return response.data.years || [];
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '利用可能な年の取得に失敗しました');
    }
  }

  /**
   * Fetches available months from construction salary data
   * @returns Promise<number[]> - Array of available months
   * @throws Error if data retrieval fails
   */
  async getConstructionAvailableMonths(): Promise<number[]> {
    try {
      const response = await this.api.get(`${API_BASE_URL}/construction-salary/months`);
      return response.data.months || [];
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '利用可能な月の取得に失敗しました');
    }
  }

  /**
   * Fetches available months for a specific year from construction salary data
   * @param year - Year to get months for
   * @returns Promise<number[]> - Array of available months for the year
   * @throws Error if data retrieval fails
   */
  async getConstructionAvailableMonthsByYear(year: number): Promise<number[]> {
    try {
      const response = await this.api.get(`${API_BASE_URL}/construction-salary/months/year/${year}`);
      return response.data.months || [];
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '指定年の利用可能な月の取得に失敗しました');
    }
  }

  /**
   * Fetches available construction sites (genbas) from construction salary data
   * @returns Promise<string[]> - Array of available construction sites
   * @throws Error if data retrieval fails
   */
  async getConstructionAvailableGenbas(): Promise<string[]> {
    try {
      const response = await this.api.get(`${API_BASE_URL}/construction-salary/genbas`);
      return response.data.genbas || [];
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '利用可能な建設現場の取得に失敗しました');
    }
  }

  /**
   * Fetches available construction sites (genbas) for a specific employee
   * @param employeeName - Employee name to filter construction sites
   * @returns Promise<string[]> - Array of available construction sites for the employee
   * @throws Error if data retrieval fails
   */
  async getConstructionAvailableGenbasByEmployee(employeeName: string): Promise<string[]> {
    try {
      const response = await this.api.get(`${API_BASE_URL}/construction-salary/genbas/employee/${encodeURIComponent(employeeName)}`);
      return response.data.genbas || [];
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '従業員の利用可能な建設現場の取得に失敗しました');
    }
  }

  /**
   * Fetches available years for a specific office employee (salary data)
   * @param employeeCode - Employee code
   * @returns Promise<number[]> - Array of available years for the employee
   * @throws Error if data retrieval fails
   */
  async getSalaryAvailableYearsByEmployee(employeeCode: string): Promise<number[]> {
    try {
      const response = await this.api.get(`${API_BASE_URL}/salary/years/employee/${encodeURIComponent(employeeCode)}`);
      return response.data.years || [];
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '従業員の利用可能な年の取得に失敗しました');
    }
  }

  /**
   * Fetches available months for a specific office employee and year (salary data)
   * @param employeeCode - Employee code
   * @param year - Year to get months for
   * @returns Promise<number[]> - Array of available months for the employee and year
   * @throws Error if data retrieval fails
   */
  async getSalaryAvailableMonthsByEmployeeAndYear(employeeCode: string, year: number): Promise<number[]> {
    try {
      const response = await this.api.get(`${API_BASE_URL}/salary/months/employee/${encodeURIComponent(employeeCode)}/year/${year}`);
      return response.data.months || [];
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '従業員の利用可能な月の取得に失敗しました');
    }
  }

  /**
   * Fetches available years for a specific office employee (bonus data)
   * @param employeeCode - Employee code
   * @returns Promise<number[]> - Array of available years for the employee
   * @throws Error if data retrieval fails
   */
  async getBonusAvailableYearsByEmployee(employeeCode: string): Promise<number[]> {
    try {
      const response = await this.api.get(`${API_BASE_URL}/bonus/years/employee/${encodeURIComponent(employeeCode)}`);
      return response.data.years || [];
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '従業員の利用可能な賞与年の取得に失敗しました');
    }
  }

  /**
   * Fetches available periods for a specific office employee and year (bonus data)
   * @param employeeCode - Employee code
   * @param year - Year to get periods for
   * @returns Promise<string[]> - Array of available periods for the employee and year
   * @throws Error if data retrieval fails
   */
  async getBonusAvailablePeriodsByEmployeeAndYear(employeeCode: string, year: number): Promise<string[]> {
    try {
      const response = await this.api.get(`${API_BASE_URL}/bonus/periods/employee/${encodeURIComponent(employeeCode)}/year/${year}`);
      return response.data.periods || [];
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '従業員の利用可能な賞与期の取得に失敗しました');
    }
  }

  /**
   * Fetches available years for an office employee by name (for accounts linked by name).
   */
  async getSalaryAvailableYearsByEmployeeName(employeeName: string): Promise<number[]> {
    try {
      const response = await this.api.get(`${API_BASE_URL}/salary/years/employee/name/${encodeURIComponent(employeeName)}`);
      return response.data.years || [];
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '従業員の利用可能な年の取得に失敗しました');
    }
  }

  async getSalaryAvailableMonthsByEmployeeNameAndYear(employeeName: string, year: number): Promise<number[]> {
    try {
      const response = await this.api.get(`${API_BASE_URL}/salary/months/employee/name/${encodeURIComponent(employeeName)}/year/${year}`);
      return response.data.months || [];
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '従業員の利用可能な月の取得に失敗しました');
    }
  }

  async getBonusAvailableYearsByEmployeeName(employeeName: string): Promise<number[]> {
    try {
      const response = await this.api.get(`${API_BASE_URL}/bonus/years/employee/name/${encodeURIComponent(employeeName)}`);
      return response.data.years || [];
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '従業員の利用可能な賞与年の取得に失敗しました');
    }
  }

  async getBonusAvailablePeriodsByEmployeeNameAndYear(employeeName: string, year: number): Promise<string[]> {
    try {
      const response = await this.api.get(`${API_BASE_URL}/bonus/periods/employee/name/${encodeURIComponent(employeeName)}/year/${year}`);
      return response.data.periods || [];
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '従業員の利用可能な賞与期の取得に失敗しました');
    }
  }

  /**
   * Fetches available years for a specific construction employee
   * @param employeeName - Employee name
   * @returns Promise<number[]> - Array of available years for the employee
   * @throws Error if data retrieval fails
   */
  async getConstructionAvailableYearsByEmployee(employeeName: string): Promise<number[]> {
    try {
      const response = await this.api.get(`${API_BASE_URL}/construction-salary/years/employee/${encodeURIComponent(employeeName)}`);
      return response.data.years || [];
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '建設従業員の利用可能な年の取得に失敗しました');
    }
  }

  /**
   * Fetches available months for a specific construction employee and year
   * @param employeeName - Employee name
   * @param year - Year to get months for
   * @returns Promise<number[]> - Array of available months for the employee and year
   * @throws Error if data retrieval fails
   */
  async getConstructionAvailableMonthsByEmployeeAndYear(employeeName: string, year: number): Promise<number[]> {
    try {
      const response = await this.api.get(`${API_BASE_URL}/construction-salary/months/employee/${encodeURIComponent(employeeName)}/year/${year}`);
      return response.data.months || [];
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '建設従業員の利用可能な月の取得に失敗しました');
    }
  }

  /**
   * Fetches available years for a specific construction site
   * @param genbaName - Construction site name
   * @returns Promise<number[]> - Array of available years for the site
   * @throws Error if data retrieval fails
   */
  async getConstructionAvailableYearsByGenba(genbaName: string): Promise<number[]> {
    try {
      const response = await this.api.get(`${API_BASE_URL}/construction-salary/years/genba/${encodeURIComponent(genbaName)}`);
      return response.data.years || [];
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '建設現場の利用可能な年の取得に失敗しました');
    }
  }

  /**
   * Fetches available years for a specific construction employee and site
   * @param employeeName - Employee name
   * @param genbaName - Construction site name
   * @returns Promise<number[]> - Array of available years for the employee and site
   * @throws Error if data retrieval fails
   */
  async getConstructionAvailableYearsByEmployeeAndGenba(employeeName: string, genbaName: string): Promise<number[]> {
    try {
      const response = await this.api.get(`${API_BASE_URL}/construction-salary/years/employee/${encodeURIComponent(employeeName)}/genba/${encodeURIComponent(genbaName)}`);
      return response.data.years || [];
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '建設従業員と現場の利用可能な年の取得に失敗しました');
    }
  }

  /**
   * Fetches available months for a specific construction site and year
   * @param genbaName - Construction site name
   * @param year - Year to get months for
   * @returns Promise<number[]> - Array of available months for the site and year
   * @throws Error if data retrieval fails
   */
  async getConstructionAvailableMonthsByGenbaAndYear(genbaName: string, year: number): Promise<number[]> {
    try {
      const response = await this.api.get(`${API_BASE_URL}/construction-salary/months/genba/${encodeURIComponent(genbaName)}/year/${year}`);
      return response.data.months || [];
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '建設現場と年の利用可能な月の取得に失敗しました');
    }
  }

  /**
   * Fetches available months for a specific construction employee, site, and year
   * @param employeeName - Employee name
   * @param genbaName - Construction site name
   * @param year - Year to get months for
   * @returns Promise<number[]> - Array of available months for the employee, site, and year
   * @throws Error if data retrieval fails
   */
  async getConstructionAvailableMonthsByEmployeeAndGenbaAndYear(employeeName: string, genbaName: string, year: number): Promise<number[]> {
    try {
      const response = await this.api.get(`${API_BASE_URL}/construction-salary/months/employee/${encodeURIComponent(employeeName)}/genba/${encodeURIComponent(genbaName)}/year/${year}`);
      return response.data.months || [];
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '建設従業員、現場、年の利用可能な月の取得に失敗しました');
    }
  }

  /**
   * Fetches available employee names from construction salary data
   * @returns Promise<string[]> - Array of available employee names
   * @throws Error if data retrieval fails
   */
  async getConstructionAvailableEmployees(): Promise<string[]> {
    try {
      const response = await this.api.get(`${API_BASE_URL}/construction-salary/employees`);
      return response.data.employees || [];
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '利用可能な従業員名の取得に失敗しました');
    }
  }

  /**
   * Fetches available employee names for a specific construction site
   * @param genbaName - Construction site name
   * @returns Promise<string[]> - Array of available employee names for the site
   * @throws Error if data retrieval fails
   */
  async getConstructionAvailableEmployeesByGenba(genbaName: string): Promise<string[]> {
    try {
      const response = await this.api.get(`${API_BASE_URL}/construction-salary/employees/genba/${encodeURIComponent(genbaName)}`);
      return response.data.employees || [];
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '指定現場の利用可能な従業員名の取得に失敗しました');
    }
  }

  /**
   * Fetches construction salary data for a specific employee, year, month, and site
   * @param employeeName - Employee name
   * @param year - Year of salary data
   * @param month - Month of salary data
   * @param genbaName - Construction site name
   * @returns Promise<any> - Construction salary data for the employee
   * @throws Error if data retrieval fails
   */
  async getConstructionSalaryData(employeeName: string, year: number, month: number, genbaName: string): Promise<any> {
    try {
      const response = await this.api.get(`${API_BASE_URL}/construction-salary/data?genbaName=${encodeURIComponent(genbaName)}&year=${year}&month=${month}&employeeName=${encodeURIComponent(employeeName)}`);
      const responseData = response.data;
      
      // The backend returns { success: true, data: [...], recordCount: n }
      if (responseData && responseData.success && responseData.data && responseData.data.length > 0) {
        return responseData.data[0];
      }
      
      return null;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '建設従業員給与データの取得に失敗しました。');
    }
  }

  /**
   * Searches for construction employee by name
   * @param employeeName - Employee name to search for
   * @returns Promise<any> - Employee data if found
   * @throws Error if search fails
   */
  async searchConstructionEmployeeByName(employeeName: string): Promise<any> {
    try {
      const response = await this.api.get(`${API_BASE_URL}/construction-salary/search/employee?employeeName=${encodeURIComponent(employeeName)}`);
      const responseData = response.data;
      
      // The backend returns { success: true, data: [...], recordCount: n }
      if (responseData && responseData.success && responseData.data && responseData.data.length > 0) {
        return responseData.data[0];
      }
      
      return null;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '建設従業員の検索に失敗しました。');
    }
  }

  async getConstructionBonusAvailableYears(): Promise<number[]> {
    try {
      const response = await this.api.get(`${API_BASE_URL}${CONSTRUCTION_BONUS_API_BASE_PATH}/years`);
      return response.data.years || [];
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '利用可能な賞与年の取得に失敗しました');
    }
  }

  async getConstructionBonusAvailablePeriods(): Promise<string[]> {
    try {
      const response = await this.api.get(`${API_BASE_URL}${CONSTRUCTION_BONUS_API_BASE_PATH}/periods`);
      return response.data.periods || [];
    } catch {
      return [];
    }
  }

  async getConstructionBonusAvailablePeriodsByYear(year: number): Promise<string[]> {
    try {
      const response = await this.api.get(
        `${API_BASE_URL}${CONSTRUCTION_BONUS_API_BASE_PATH}/periods/year/${year}`
      );
      return response.data.periods || [];
    } catch {
      return [];
    }
  }

  async getConstructionBonusAvailableGenbas(): Promise<string[]> {
    try {
      const response = await this.api.get(`${API_BASE_URL}${CONSTRUCTION_BONUS_API_BASE_PATH}/genbas`);
      return response.data.genbas || [];
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '利用可能な賞与現場の取得に失敗しました');
    }
  }

  async getConstructionBonusAvailableEmployees(): Promise<string[]> {
    try {
      const response = await this.api.get(`${API_BASE_URL}${CONSTRUCTION_BONUS_API_BASE_PATH}/employees`);
      return response.data.employees || [];
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '利用可能な賞与従業員名の取得に失敗しました');
    }
  }

  async getConstructionBonusData(
    employeeName: string,
    year: number,
    period: string,
    genbaName?: string
  ): Promise<any> {
    try {
      const params = new URLSearchParams();
      params.append('year', String(year));
      params.append('period', period);
      params.append('employeeName', employeeName);
      if (genbaName?.trim()) {
        params.append('genbaName', genbaName.trim());
      }
      const response = await this.api.get(
        `${API_BASE_URL}${CONSTRUCTION_BONUS_API_BASE_PATH}/data?${params.toString()}`
      );
      const responseData = response.data;
      if (responseData?.success && responseData.data?.length > 0) {
        return responseData.data[0];
      }
      return null;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '建設従業員賞与データの取得に失敗しました。');
    }
  }

  async searchConstructionBonusEmployeeByName(employeeName: string): Promise<any> {
    try {
      const response = await this.api.get(
        `${API_BASE_URL}${CONSTRUCTION_BONUS_API_BASE_PATH}/search/employee?employeeName=${encodeURIComponent(employeeName)}`
      );
      const responseData = response.data;
      if (responseData?.success && responseData.data?.length > 0) {
        return responseData.data[0];
      }
      return null;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '建設従業員賞与の検索に失敗しました。');
    }
  }

  async getConstructionBonusAvailableGenbasByEmployee(employeeName: string): Promise<string[]> {
    try {
      const response = await this.api.get(
        `${API_BASE_URL}${CONSTRUCTION_BONUS_API_BASE_PATH}/genbas/employee/${encodeURIComponent(employeeName)}`
      );
      return response.data.genbas || [];
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '利用可能な賞与現場の取得に失敗しました');
    }
  }

  async getConstructionBonusAvailableYearsByEmployee(employeeName: string): Promise<number[]> {
    try {
      const response = await this.api.get(
        `${API_BASE_URL}${CONSTRUCTION_BONUS_API_BASE_PATH}/years/employee/${encodeURIComponent(employeeName)}`
      );
      return response.data.years || [];
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '利用可能な賞与年の取得に失敗しました');
    }
  }

  async getConstructionBonusAvailablePeriodsByEmployeeAndYear(
    employeeName: string,
    year: number
  ): Promise<string[]> {
    try {
      const response = await this.api.get(
        `${API_BASE_URL}${CONSTRUCTION_BONUS_API_BASE_PATH}/periods/employee/${encodeURIComponent(employeeName)}/year/${year}`
      );
      return response.data.periods || [];
    } catch {
      return [];
    }
  }

  async getConstructionBonusAvailableYearsByGenba(genbaName: string): Promise<number[]> {
    try {
      const response = await this.api.get(
        `${API_BASE_URL}${CONSTRUCTION_BONUS_API_BASE_PATH}/years/genba/${encodeURIComponent(genbaName)}`
      );
      return response.data.years || [];
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '利用可能な賞与年の取得に失敗しました');
    }
  }

  async getConstructionBonusAvailableYearsByEmployeeAndGenba(
    employeeName: string,
    genbaName: string
  ): Promise<number[]> {
    try {
      const response = await this.api.get(
        `${API_BASE_URL}${CONSTRUCTION_BONUS_API_BASE_PATH}/years/employee/${encodeURIComponent(employeeName)}/genba/${encodeURIComponent(genbaName)}`
      );
      return response.data.years || [];
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '利用可能な賞与年の取得に失敗しました');
    }
  }

  async getConstructionBonusAvailablePeriodsByGenbaAndYear(
    genbaName: string,
    year: number
  ): Promise<string[]> {
    try {
      const response = await this.api.get(
        `${API_BASE_URL}${CONSTRUCTION_BONUS_API_BASE_PATH}/periods/genba/${encodeURIComponent(genbaName)}/year/${year}`
      );
      return response.data.periods || [];
    } catch {
      return [];
    }
  }

  async getConstructionBonusAvailablePeriodsByEmployeeAndGenbaAndYear(
    employeeName: string,
    genbaName: string,
    year: number
  ): Promise<string[]> {
    try {
      const response = await this.api.get(
        `${API_BASE_URL}${CONSTRUCTION_BONUS_API_BASE_PATH}/periods/employee/${encodeURIComponent(employeeName)}/genba/${encodeURIComponent(genbaName)}/year/${year}`
      );
      return response.data.periods || [];
    } catch {
      return [];
    }
  }
}

// Export singleton instance of the service
export const fileUploadService = new FileUploadService();
export default fileUploadService; 

/**
 * Downloads CSV data based on specified options
 * 
 * @param options - Configuration object specifying what data to download
 * @returns Promise<void> - Resolves when download completes
 * @throws Error if download fails
 * 
 * This function:
 * - Constructs the appropriate API endpoint URL
 * - Fetches data from the server
 * - Creates a downloadable blob
 * - Triggers browser download with appropriate filename
 */
export const downloadCsv = async (options: CsvDownloadOptions): Promise<void> => {
  try {
    const { dataType, year, month, period, department, employeeCode } = options;
    
    // Build download URL with filters
    let url = `${API_BASE_URL}/${dataType}/csv?`;
    const params = new URLSearchParams();
    
    // Add optional filters to query parameters
    if (year) params.append('year', year.toString());
    if (month) params.append('month', month.toString());
    if (period) params.append('period', period);
    if (department) params.append('department', department);
    if (employeeCode) params.append('employeeCode', employeeCode);
    
    url += params.toString();
    
    // Get JWT token for authorization
    const token = sessionStorage.getItem('authToken');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Fetch CSV data from server
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });
    
    // Handle various error responses
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('指定された条件のデータが見つかりませんでした。');
      }
      throw new Error('CSVダウンロードに失敗しました。');
    }
    
    // Extract filename from response headers or use default
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `${dataType}_data.csv`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    
    // Create blob and trigger download
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
    
  } catch (error) {
    throw error;
  }
}; 
/**
 * Fetches list of available departments from the server
 * 
 * @returns Promise<string[]> - Array of department names
 * @throws Error if department fetch fails
 * 
 * Used for populating department dropdowns and filters
 */
export const fetchDepartments = async (): Promise<string[]> => {
  try {
    // Get JWT token for authorization
    const token = sessionStorage.getItem('authToken');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(`${API_BASE_URL}/departments`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error('部署情報の取得に失敗しました。');
    }

    const data = await response.json();
    return data.departments || [];
  } catch (error) {
    throw error;
  }
};

/**
 * Fetches employee information from the server
 * 
 * @param department - Optional department filter
 * @returns Promise<Employee[]> - Array of employee objects
 * @throws Error if employee fetch fails
 * 
 * Used for populating employee dropdowns and data tables
 */
export const fetchEmployees = async (department?: string): Promise<Employee[]> => {
  try {
    let url = `${API_BASE_URL}/employees`;
    // Add department filter if specified
    if (department) {
      url += `?department=${encodeURIComponent(department)}`;
    }

    // Get JWT token for authorization
    const token = sessionStorage.getItem('authToken');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error('従業員情報の取得に失敗しました。');
    }

    const data = await response.json();
    
    // Handle both response structures: data.employees or data directly
    // This provides flexibility for different API response formats
    let employees = [];
    if (data.employees && Array.isArray(data.employees)) {
      employees = data.employees;
    } else if (Array.isArray(data)) {
      employees = data;
    }
    
    return employees;
  } catch (error) {
    throw error;
  }
};


